import { Box, Divider, Paper, Tooltip, Typography } from "@mui/material";
import { useMemo, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FiFilter, FiGrid, FiRefreshCw, FiX } from "react-icons/fi";
import AddToSupplyChainButton from "../../../components/button/AddToSuplyChainButton";
import ColumnVisibilityButton from "../../../components/button/ColumnVisibilityButton";
import PivotableBiaxialBarChart from "../../../components/chart/PivotableBiaxialBarChart";
import PivotablePieChart from "../../../components/chart/PivotablePieChart";
import ScatterChart from "../../../components/chart/ScatterChart";
import { TableFooterContainer } from "../../../components/container/TableFooterContainer";
import { TableHeaderContainer } from "../../../components/container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../../../components/container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../../../components/container/TableTitleContainer";
import ColumnMenu from "../../../components/context-menu/ColumnMenu";
import AgGrid, { GridPagination } from "../../../components/grid/AgGrid";
import {
  centerAlignColDef,
  defaultColDef,
  floatFormater,
  leftAlignColDef,
  rightAlignColDef,
  translationFormater,
} from "../../../components/grid/columnDefinitions/columnDefinitions";
import CheckBox from "../../../components/input/CheckBox";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import SearchBox from "../../../components/input/SearchBox";
import RefreshButton from "../../../components/button/RefreshButton";
import { Supplier_Reports } from "../../../db_access/supplier_reports";
import { Supply_chain } from "../../../db_access/supply_chain";
import { ReportTypes } from "../ReportTypes";
import AdditionalNotes from "./AdditionalNotes";
import { ORG_PALETTE, getOrgTokens } from "../../../theme/style/organizationPalette";

const ReportDetailGrid = ({ companies, selectedCompanyIds, emissionWeight, setSelectedCompanyIds }) => {
  const { t } = useTranslation();
  const { columnVisibilities, setRefresh, showSnackbar } = useContextProvider();
  const table_ref = useRef();
  const header_ref = useRef();
  const colVisibility_key = "supplier_directory_dashboard_report_table";
  const [showColVisibilityMenu, setShowColVisibilityMenu] = useState(false);
  const [graphView, setGraphView] = useState(false);
  const [selectedReportIds, setSelectedReportIds] = useState([]);
  const [searchText, setSearchText] = useState("");
  const addToSupplyChain = () => {
    console.log(
      "Add to Supply Chain",
      selectedCompanyIds,
      selectedReportIds,
      companies.filter((c) => selectedCompanyIds.includes(c.id)),
      companies.find((c) => selectedCompanyIds.includes(c.id))?.reports.filter((r) => selectedReportIds.includes(r.id))
    );
    Supply_chain.add_from_supplier_report(selectedReportIds)
      .then(() => {
        setRefresh((r) => !r);
        showSnackbar?.(`${selectedReportIds.length} ${t("report(s) added to supply chain successfully.")}`, "success");
      })
      .catch((err) => {
        showSnackbar?.(t("Failed to add to supply chain."), "error");
      });
  };
  const columnDefs = useMemo(() => {
    const isHidden = (field) => !columnVisibilities[colVisibility_key]?.includes(field);
    const onCellValueChanged = (params) => {
      const { data } = params;
      console.log(params);
      Supplier_Reports.update_type(data.id, data.type).finally(() => setRefresh((r) => !r));
    };
    const colDefs = [
      { field: "name", headerName: t("Name"), ...leftAlignColDef, ...defaultColDef },
      { field: "year", headerName: t("Year"), ...centerAlignColDef, ...defaultColDef },
      { field: "quantity", headerName: t("Quantity"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { field: "cost", headerName: t("Cost"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { field: "cost_per_unit", headerName: t("Cost Per Unit"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { field: "gwp", headerName: t("GWP"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { field: "gwp_per_unit", headerName: t("GWP Per Unit"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { field: "functional_unit", headerName: t("Functional Unit"), ...centerAlignColDef, ...defaultColDef },
      { field: "other_unit", headerName: t("Other Unit"), ...centerAlignColDef, ...defaultColDef },
      {
        field: "type",
        headerName: t("Type"),
        ...centerAlignColDef,
        ...defaultColDef,
        editable: true,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: ReportTypes },
        ...translationFormater(t),
        onCellValueChanged: (params) => onCellValueChanged(params),
      },
      { field: "level", headerName: t("Level"), ...centerAlignColDef, ...defaultColDef, ...translationFormater(t) },
      { field: "scope", headerName: t("Scope"), ...centerAlignColDef, ...defaultColDef, ...translationFormater(t) },
      { field: "last_modified", headerName: t("Last Modified"), ...rightAlignColDef, ...defaultColDef },
      { field: "created_at", headerName: t("Created At"), ...rightAlignColDef, ...defaultColDef },
    ];
    return colDefs.map((col) => {
      if (col.editable) {
        return { ...col, onCellValueChanged, hide: isHidden(col.field) };
      }
      return { ...col, hide: isHidden(col.field) };
    });
  }, [columnVisibilities, t, setRefresh]);

  const currentReports = useMemo(() => {
    return companies.find((c) => c.id === selectedCompanyIds[0])?.reports || [];
  }, [companies, selectedCompanyIds]);

  const totalMetrics = useMemo(() => {
    const totalGwp = currentReports.reduce((sum, r) => sum + (Number(r.gwp) || 0), 0);
    const totalCost = currentReports.reduce((sum, r) => sum + (Number(r.cost) || 0), 0);
    return {
      gwp: totalGwp.toLocaleString(undefined, { maximumFractionDigits: 0 }),
      cost: totalCost.toLocaleString(undefined, { maximumFractionDigits: 0 })
    };
  }, [currentReports]);

  useEffect(() => {
    if (table_ref.current?.api) {
      table_ref.current.api.setGridOption("quickFilterText", searchText);
    }
  }, [searchText]);
  return (
    <Box sx={{ display: selectedCompanyIds.length === 1 ? "block" : "none", mt: 4 }}>
      <Paper variant="Table Container">
        <TableHeaderContainer>
          <TableTitleContainer>
            <Typography variant="Table Title">
              {companies.find((c) => c.id === selectedCompanyIds[0])?.name} - {t("Granular Submission Breakdown")} ({currentReports.length})
            </Typography>
          </TableTitleContainer>
          <TableHeaderToolsContainer ref={header_ref}>
            <Box sx={{ width: 450 }}>
              <SearchBox text={searchText} setText={setSearchText} placeholder={t("Search reports...")} width={450} />
            </Box>
            <ColumnVisibilityButton onClick={() => setShowColVisibilityMenu((p) => !p)} />
            <RefreshButton setRefresh={setRefresh} />
            <Box onClick={() => setSelectedCompanyIds && setSelectedCompanyIds([])} sx={{ p: 1, ml: 1, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "10px", "&:hover": { bgcolor: "action.hover" } }}>
              <FiX size={18} color="#64748b" />
            </Box>
          </TableHeaderToolsContainer>
        </TableHeaderContainer>
        <Box>
          <AgGrid
            ref={table_ref}
            rowData={currentReports}
            columnDefs={columnDefs}
            onSelectionChanged={(event) => {
              setSelectedReportIds(event.api.getSelectedRows().map((r) => r.id));
            }}
            domLayout="autoHeight"
            footerLeftContent={
              <Box sx={{ display: 'flex', alignItems: 'center', ml: -1 }}>
                <CheckBox 
                  checked={graphView} 
                  onChange={(e) => setGraphView(e.target.checked)} 
                  label={t("Global View")} 
                />
              </Box>
            }
            footerRightContent={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <AddToSupplyChainButton
                  disabled={selectedReportIds.length === 0}
                  onClick={addToSupplyChain}
                  sx={{
                    height: 44,
                    px: 4,
                    borderRadius: "12px",
                    boxShadow: selectedReportIds.length > 0 ? "0 4px 14px rgba(22, 163, 74, 0.25)" : "none"
                  }}
                />
              </Box>
            }
          />
        </Box>

        <ColumnMenu
          anchorRef={header_ref}
          visible={showColVisibilityMenu}
          onClose={() => setShowColVisibilityMenu(false)}
          colDefs={columnDefs}
          colVisibility_key={colVisibility_key}
          defaultCols={["name", "year", "quantity", "cost", "cost_per_unit", "gwp", "gwp_per_unit", "type", "level", "last_modified"]}
        />
      </Paper>

      {/* Additional Notes section below AgGrid but inside the same Paper block if needed, or separately */}
      {selectedReportIds.length === 1 && (
        <Box sx={{ mt: 2, p: 3, bgcolor: "background.paper", borderRadius: ORG_PALETTE.radius.card, border: "1px solid", borderColor: "divider" }}>
          <Typography sx={{ fontFamily: "Poppins", fontSize: 13, fontWeight: 700, color: "#64748b", mb: 1, letterSpacing: 0.5 }}>
            Supplier Notes
          </Typography>
          <AdditionalNotes note={currentReports.find(r => r.id === selectedReportIds[0])?.notes} />
        </Box>
      )}
    </Box>
  );
};

export default ReportDetailGrid;
